import React, { useMemo } from "react"; //{ useEffect, useMemo }
import { PositionBadge } from "../../CrewSchedule/PositionBadge";
import { Grid, Typography } from "@mui/material"; //Theme, useTheme
import { IPairingPosition } from "@vmosolutions/oc-interfaces"; //IPosition
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IPairingPositionCell {
  positions?: IPairingPosition[];
  displayCrewName?: boolean;
  retainDisplayOrder?: boolean;
  positionTitles?: string[]; // ["CA", "FO"]
}

//  CA - Captain
//  FO - First Officer
//  IC - In-Charge FA
//  FA - Flight Attendant
//  CP - Check Captain
//  TC - Training Captain
//  DM - DMS
//  RA - Radar Operator

/**
 * Accepts array of pairing event groups and displays them in a condensed format for the search table.
 * @param props - Array of PairingEventGroups
 */
export const PairingPositionCell = (props: IPairingPositionCell): JSX.Element => {
  const { positions, displayCrewName, retainDisplayOrder, positionTitles } = props;

  const positionsArray = useSelector((state: IRootState) => state.structures.customerStructure.positions);

  // Deprecated hardcoded position info.
  //const groups: any = {
  //  filled: { CA: [], FO: [], IC: [], FA: [], CP: [], TC: [], DM: [], RA: [], NF: [] },
  //  empty: { CA: [], FO: [], IC: [], FA: [], CP: [], TC: [], DM: [], RA: [], NF: [] },
  //};

  const positionGroups = useMemo(() => {
    const groups: any = { filled: {}, empty: {} };

    if (!positions?.length) return groups;

    // Add empty
    positionsArray
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .forEach((pos) => {
        groups.filled[pos.title] = [];
        groups.empty[pos.title] = [];
      });

    // const positionDisplayOrder = positionsArray.reduce((obj, pos) => {
    //   obj[pos.title] = pos.displayOrder;
    //   return obj;
    // }, {});

    // Fill positions that are occupied.
    positions.forEach((pos: IPairingPosition) => {
      if (pos.crew_id && pos.position) {
        groups.filled[pos.position]?.push(pos);
      } else if (!pos.crew_id && pos.position) {
        groups.empty[pos.position]?.push(pos);
      }
    });

    return groups;
  }, [positions, positionsArray]);

  /**
   * Return array of Sorted position badges (displays filled positions first, then empty)
   * or if the prop of retainDisplayOrder is true, display in the order the positions were passed in
   */
  const renderBadgeGroups = useMemo(() => {
    const groups: any = [];

    if (retainDisplayOrder) {
      positions.forEach((crewPos, index) => {
        const crewNames = `${crewPos.crew_id ? `${crewPos.givenName ?? ""} ${crewPos.surname ?? ""}` : "Unassigned"}`;
        groups.push(
          <Grid
            item
            key={`position_badges_${index}_${crewPos.crew_id ? "filled" : "empty"}`}
            xs={"auto"}
            sx={{ display: "inline-flex" }}
          >
            {displayCrewName ? (
              <>
                <div style={{ margin: "0px 5px" }}>
                  <PositionBadge pairingPosition={crewPos} count={1} />
                </div>
                <Typography component="span" style={{ fontSize: 12 }}>
                  {crewNames}
                </Typography>
              </>
            ) : (
              <Tooltip enterDelay={500} placement="top" title={crewNames}>
                <div>
                  <PositionBadge pairingPosition={crewPos} count={1} />
                </div>
              </Tooltip>
            )}
          </Grid>
        );
      });

      return groups.length > 0 ? groups : <div>None</div>;
    } else {
      const { filled, empty } = positionGroups;

      for (const key in filled) {
        if (filled[key].length > 0) {
          let crewNames = "";
          filled[key].forEach(
            (pos: IPairingPosition, index: number) =>
              (crewNames += `${index > 0 ? ", " : ""}${pos.givenName ?? ""} ${pos.surname ?? ""}`)
          );
          groups.push(
            <Grid item key={`position_badges_${key}_filled`} xs={"auto"} sx={{ display: "inline-flex" }}>
              {displayCrewName ? (
                <>
                  <div style={{ margin: "0px 5px" }}>
                    <PositionBadge pairingPosition={filled[key][0]} count={filled[key].length} filled={true} />
                  </div>
                  <Typography component="span" style={{ fontSize: 12 }}>
                    {crewNames}
                  </Typography>
                </>
              ) : (
                <Tooltip enterDelay={500} placement="top" title={crewNames}>
                  <div>
                    <PositionBadge pairingPosition={filled[key][0]} count={filled[key].length} filled={true} />
                  </div>
                </Tooltip>
              )}
            </Grid>
          );
        }
      }

      for (const key in empty) {
        if (empty[key].length > 0) {
          let crewNames = "";
          empty[key].forEach(
            (pos: IPairingPosition, index: number) =>
              (crewNames += `${index > 0 ? ", " : ""}${pos.givenName ?? ""} ${pos.surname ?? ""}`)
          );
          groups.push(
            <Grid item key={`position_badges_${key}_empty`} xs={"auto"}>
              {displayCrewName ? (
                <div style={{ margin: "0px 5px" }}>
                  <PositionBadge pairingPosition={empty[key][0]} count={empty[key].length} />
                </div>
              ) : (
                <PositionBadge pairingPosition={empty[key][0]} count={empty[key].length} />
              )}
            </Grid>
          );
        }
      }

      return groups.length > 0 ? groups : <div>None</div>;
    }
  }, [retainDisplayOrder, positions, displayCrewName, positionGroups]);

  return (
    <Grid container>
      {!!positions?.length && renderBadgeGroups}
      {!!positionTitles?.length &&
        positionTitles.map((title) => {
          const position = positionsArray.find((pos) => pos.title === title);
          return (
            <div style={{ margin: 2 }} key={`position_title_${title}`}>
              <PositionBadge position={position} />
            </div>
          );
        })}
    </Grid>
  );
};
